import { deleteBillingAddress } from "@gik/api/users/billingAddresses";
import type { GikBillingAddressModel } from "@gik/core/models/gik/BillingAddress";
import type { BaseUserWithLocation } from "@gik/core/models/gik/User";
import type { UIComponent } from "@gik/core/types/UI";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { storage } from "@gik/core/utils/Storage";
import { Button } from "@gik/ui-legacy/Button";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon";
import { UI } from "@gik/ui-legacy/UIManager";
import PencilIcon from "@heroicons/react/solid/PencilIcon";
import TrashIcon from "@heroicons/react/solid/TrashIcon";
import React from "react";
import { openAddEditUserBillingAddressModal } from "./openEditUserBillingAddressModal";

export type BillingAddressBoxProps = {
	user: BaseUserWithLocation;
	allowDelete?: boolean;
	billingAddress?: GikBillingAddressModel;
	onSuccess?: () => void;
	onDelete?: () => void;
} & UIComponent;

export function BillingAddressBox({
	user,
	children,
	className,
	allowDelete,
	billingAddress,
	onSuccess,
	onDelete,
	...otherProps
}: React.PropsWithChildren<BillingAddressBoxProps>): React.ReactElement {
	const bem = useBemCN("billing-address-box");

	const openDialog = React.useCallback(() => {
		const response = openAddEditUserBillingAddressModal({
			user,
			billingAddress,
		});

		if (response) onSuccess?.();
	}, [user, billingAddress, onSuccess]);

	const handleDelete = React.useCallback(async () => {
		const confirmResponse = await UI.confirm(
			"Are you sure you want to remove your billing details ?",
		);
		if (confirmResponse) {
			await deleteBillingAddress(user.id, billingAddress.id);
			onDelete?.();
		}
	}, [user, billingAddress, onDelete]);

	React.useEffect(() => {
		const openDialogs: string[] = storage.getJSON("open-dialogs");
		if (openDialogs?.indexOf("addEditUserBillingAddressForm") > -1) {
			openDialog();
		}
		//eslint-disable-next-line
	}, []);

	return (
		<div {...bem(null, null, className)} {...otherProps}>
			<div {...bem("main")}>
				<span {...bem("title")}>Billing Address</span>
				<address {...bem("address")}>
					{billingAddress.companyName ||
						billingAddress.firstName + " " + billingAddress.lastName}{" "}
					<br />
					{billingAddress.address1}
				</address>
			</div>
			<div {...bem("actions")}>
				<Button
					{...bem("btn-edit")}
					pill
					variant="icon"
					size="base"
					onClick={openDialog}
					customIconSize
				>
					<SvgIcon Icon={PencilIcon} />
				</Button>
				{allowDelete && (
					<Button
						{...bem("btn-delete")}
						pill
						variant="icon"
						size="base"
						onClick={handleDelete}
						customIconSize
					>
						<SvgIcon Icon={TrashIcon} />
					</Button>
				)}
			</div>
		</div>
	);
}
